// The wizard's frame: header (back link, title, context chips, GOAL chip,
// Advanced escape hatch) and footer (Back / note / Continue). The page owns
// all state and handlers; this only renders. Reusable by future wizards.
//
// Props:
//   title: string
//   backLabel?: string (default '← Home'), onBack: () => void
//   chips?: [{ label, fg, bg, onClick?, title? }] — context pills after the
//       title; a chip with onClick renders clickable (pointer cursor, hover
//       title) — additive, chips without one are byte-identical to before
//   goal?: { label }                       — amber GOAL chip, right-aligned
//   onAdvanced?: () => void                — header link when present
//   advancedLabel?: string                 — its label (default 'Advanced ↗')
//   footer?: { backLabel, onBack, note, continueLabel, onContinue,
//              continueDisabled?, continueStyle? }
//   theme?: { display, accent, link, shell, body }
//       Omit for the ContentIQ "Gallery" look (the default). The Programs
//       creation wizard passes NeTrA's own navy + system font so it doesn't drag
//       the ContentIQ display face and palette into a core NeTrA page — the two
//       are deliberately separate visual languages, but the frame itself is the
//       same job in both, so it's one component rather than two.
//   children                               — the step content
import React from 'react';
import {
  BRAND, FONTS, INK, NEUTRAL, STATE,
  buttonPrimary, buttonQuiet, pageShell, pill,
} from '../utils/wizardTheme';

const DEFAULT_THEME = {
  display: FONTS.display, accent: BRAND.button, link: BRAND.link,
  shell: pageShell, body: FONTS.body,
};

export default function WizardChrome({
  title, backLabel = '← Home', onBack, chips = [], goal, onAdvanced, advancedLabel = 'Advanced ↗',
  footer, theme, children,
}) {
  const t = { ...DEFAULT_THEME, ...(theme || {}) };
  return (
    <div style={{ ...t.shell, display: 'flex', flexDirection: 'column' }}>
      <div style={{ padding: '14px 40px', display: 'flex', alignItems: 'center', gap: '14px', flexWrap: 'wrap' }}>
        <span onClick={onBack}
          style={{ fontSize: '13px', fontWeight: 600, color: t.link, cursor: 'pointer' }}>
          {backLabel}
        </span>
        <div style={{ fontFamily: t.display, fontWeight: 600, fontSize: '17px' }}>{title}</div>
        {chips.map(c => (
          <span key={c.label} onClick={c.onClick} title={c.title}
            style={{ ...pill(c.fg, c.bg), ...(c.onClick ? { cursor: 'pointer' } : {}) }}>
            {c.label}
          </span>
        ))}
        <div style={{ flexGrow: 1 }} />
        {goal && (
          <span style={{ ...pill(STATE.gate.main, STATE.gate.tint), fontWeight: 700 }}>
            {goal.label}
          </span>
        )}
        {onAdvanced && (
          <span onClick={onAdvanced}
            style={{ fontSize: '12px', color: INK.secondary, fontWeight: 600, cursor: 'pointer' }}>
            {advancedLabel}
          </span>
        )}
      </div>

      <div style={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>{children}</div>

      {footer && (
        <div style={{
          borderTop: `1px solid ${NEUTRAL.borderSoft}`, padding: '14px 40px',
          display: 'flex', alignItems: 'center', gap: '12px',
        }}>
          {footer.backLabel && (
            <button onClick={footer.onBack} style={{ ...buttonQuiet, fontFamily: t.body }}>
              {footer.backLabel}
            </button>
          )}
          <div style={{ flexGrow: 1 }} />
          {footer.note && (
            <span style={{ fontSize: '12px', color: INK.muted }}>{footer.note}</span>
          )}
          {footer.continueLabel && (
            <button
              onClick={footer.onContinue}
              disabled={!!footer.continueDisabled}
              style={{
                ...buttonPrimary(),
                background: t.accent, fontFamily: t.body,
                ...(footer.continueDisabled
                  ? { background: NEUTRAL.controlSoft, color: INK.muted, boxShadow: 'none', cursor: 'default' }
                  : {}),
                ...(footer.continueStyle || {}),
              }}>
              {footer.continueLabel}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
